import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, TextField, Button, Autocomplete, 
  Grid, Card, CardContent, Divider, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Tabs, Tab, IconButton,
  List, ListItem, ListItemText, useTheme
} from '@mui/material';
import { 
  Article as ArticleIcon, 
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  BarChart as BarChartIcon,
  CompareArrows as CompareArrowsIcon,
  Newspaper as NewspaperIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import FundamentalNodeService from '../services/FundamentalNodeService';

// Helper component for displaying metric with change indicator
const MetricDisplay = ({ label, value, change, unit = '', isPercentage = false, reverseColors = false }) => {
  const isPositive = parseFloat(change) > 0;
  const isNegative = parseFloat(change) < 0;
  const color = (reverseColors ? !isPositive : isPositive) ? 'success.main' : (isNegative ? 'error.main' : 'text.secondary');
  
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h6" component="span">
          {value}{unit}
        </Typography>
        {change && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            {isPositive ? <TrendingUpIcon fontSize="small" sx={{ color }} /> : 
             isNegative ? <TrendingDownIcon fontSize="small" sx={{ color }} /> : null}
            <Typography variant="body2" sx={{ color }}>
              {isPositive ? '+' : ''}{change}{isPercentage ? '%' : ''}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Helper component for news items
const NewsItem = ({ title, date, source, sentiment }) => {
  const theme = useTheme();
  const sentimentColor = sentiment > 0.5 ? theme.palette.success.main : 
                          sentiment < -0.2 ? theme.palette.error.main : 
                          theme.palette.warning.main;
  
  return (
    <ListItem divider>
      <ListItemText 
        primary={
          <Typography variant="subtitle2">{title}</Typography>
        }
        secondary={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
            <Box component="span">
              <Typography variant="caption" component="span" color="text.secondary">
                {date} • {source}
              </Typography>
            </Box>
            <Box component="span">
              <Chip 
                label={sentiment > 0.5 ? 'Positive' : sentiment < -0.2 ? 'Negative' : 'Neutral'} 
                size="small"
                sx={{ 
                  bgcolor: `${sentimentColor}20`,
                  color: sentimentColor,
                  fontWeight: 500
                }}
              />
            </Box>
          </Box>
        }
      />
    </ListItem>
  );
};

function FundamentalAnalysis() {
  const [symbol, setSymbol] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fundamentalData, setFundamentalData] = useState(null);
  
  const popularSymbols = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'BTC', 'ETH', 'NVDA'];
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const fetchFundamentalData = async (sym) => {
    if (!sym) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // This will communicate with the fundamental nodes through our service
      const data = await FundamentalNodeService.getFundamentalAnalysis(sym);
      setFundamentalData(data);
    } catch (err) {
      console.error('Error fetching fundamental data:', err);
      setError('Failed to fetch fundamental data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSymbolSubmit = () => {
    if (symbol) {
      fetchFundamentalData(symbol);
    }
  };
  
  // Load initial data when the component mounts
  useEffect(() => {
    // Default to AAPL on first load
    fetchFundamentalData('AAPL');
  }, []);
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{
        mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
        p: 2, borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ArticleIcon sx={{ fontSize: 28, mr: 1, color: '#00FF94' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
            Fundamental Analysis
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Autocomplete
            value={symbol}
            onChange={(event, newValue) => {
              setSymbol(newValue);
              if (newValue) handleSymbolSubmit();
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => {
              setInputValue(newInputValue);
            }}
            options={popularSymbols}
            freeSolo
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Symbol" 
                variant="outlined" 
                size="small"
                sx={{ width: 150 }}
              />
            )}
          />
          <Button 
            variant="contained" 
            startIcon={<SearchIcon />}
            onClick={handleSymbolSubmit}
            disabled={!symbol || loading}
          >
            Analyze
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Analyzing {symbol} fundamentals...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
      ) : fundamentalData ? (
        <Box>
          {/* Company Overview */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h5">{fundamentalData.name} ({fundamentalData.symbol})</Typography>
                <Typography variant="body2" color="text.secondary">
                  Last updated: {new Date(fundamentalData.updatedAt).toLocaleString()}
                </Typography>
              </Box>
              <IconButton onClick={() => fetchFundamentalData(fundamentalData.symbol)}>
                <RefreshIcon />
              </IconButton>
            </Box>
            
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<BarChartIcon />} label="Financials" />
              <Tab icon={<NewspaperIcon />} label="News & Sentiment" />
              <Tab icon={<AccountBalanceIcon />} label="Valuation" />
              <Tab icon={<CompareArrowsIcon />} label="Peer Comparison" />
            </Tabs>
            
            {/* Tab content with data from fundamental nodes */}
            <Box sx={{ p: 2, minHeight: '300px' }}>
              {/* Financials Tab */}
              {tabValue === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Key Financial Metrics</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Revenue & Earnings</Typography>
                          <MetricDisplay 
                            label="Revenue"
                            value={fundamentalData.financials.revenue.value}
                            change={fundamentalData.financials.revenue.change}
                            unit={fundamentalData.financials.revenue.unit}
                          />
                          <MetricDisplay 
                            label="Earnings"
                            value={fundamentalData.financials.earnings.value}
                            change={fundamentalData.financials.earnings.change}
                            unit={fundamentalData.financials.earnings.unit}
                          />
                          <MetricDisplay 
                            label="EPS"
                            value={fundamentalData.financials.eps.value}
                            change={fundamentalData.financials.eps.change}
                            unit={fundamentalData.financials.eps.unit}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Financial Health</Typography>
                          <MetricDisplay 
                            label="Free Cash Flow"
                            value={fundamentalData.financials.freeCashFlow.value}
                            change={fundamentalData.financials.freeCashFlow.change}
                            unit={fundamentalData.financials.freeCashFlow.unit}
                          />
                          <MetricDisplay 
                            label="Debt to Equity"
                            value={fundamentalData.financials.debtToEquity.value}
                            change={fundamentalData.financials.debtToEquity.change}
                            reverseColors={fundamentalData.financials.debtToEquity.isGoodWhenNegative}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Profitability</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <MetricDisplay 
                                label="Gross Margin"
                                value={fundamentalData.financials.grossMargin.value}
                                change={fundamentalData.financials.grossMargin.change}
                                unit="%"
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <MetricDisplay 
                                label="Operating Margin"
                                value={fundamentalData.financials.operatingMargin.value}
                                change={fundamentalData.financials.operatingMargin.change}
                                unit="%"
                              />
                            </Grid>
                          </Grid>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            Financial data for {fundamentalData.financials.revenue.period}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* News & Sentiment Tab */}
              {tabValue === 1 && (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                      <Typography variant="h6" gutterBottom>Latest News</Typography>
                      <Paper variant="outlined" sx={{ mb: 3 }}>
                        <List>
                          {fundamentalData.news.map((newsItem, index) => (
                            <NewsItem 
                              key={index}
                              title={newsItem.title}
                              date={newsItem.date}
                              source={newsItem.source}
                              sentiment={newsItem.sentiment}
                            />
                          ))}
                        </List>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Typography variant="h6" gutterBottom>Sentiment Analysis</Typography>
                      <Card variant="outlined" sx={{ mb: 3 }}>
                        <CardContent>
                          <Typography variant="subtitle2">Overall Sentiment</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Box 
                              sx={{ 
                                height: 15, 
                                width: '100%', 
                                bgcolor: 'background.paper',
                                borderRadius: 5,
                                position: 'relative',
                                mb: 1
                              }}
                            >
                              <Box 
                                sx={{ 
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  height: '100%',
                                  width: `${parseFloat(fundamentalData.sentimentAnalysis.overallSentiment.score) * 100}%`,
                                  bgcolor: parseFloat(fundamentalData.sentimentAnalysis.overallSentiment.score) > 0.6 ? 'success.main' : 
                                          parseFloat(fundamentalData.sentimentAnalysis.overallSentiment.score) > 0.4 ? 'warning.main' : 'error.main',
                                  borderRadius: 5
                                }}
                              />
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                            <Typography variant="body2" color="error.main">Bearish</Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {(parseFloat(fundamentalData.sentimentAnalysis.overallSentiment.score) * 100).toFixed(0)}%
                            </Typography>
                            <Typography variant="body2" color="success.main">Bullish</Typography>
                          </Box>
                          
                          <Divider sx={{ my: 2 }} />
                          
                          <Typography variant="subtitle2" gutterBottom>Sentiment by Source</Typography>
                          <Grid container spacing={1}>
                            {Object.entries(fundamentalData.sentimentAnalysis.sources).map(([source, score]) => (
                              <Grid item xs={6} key={source}>
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                    {source}
                                  </Typography>
                                  <Box 
                                    sx={{ 
                                      height: 8, 
                                      width: '100%', 
                                      bgcolor: 'background.paper',
                                      borderRadius: 5,
                                      position: 'relative',
                                      mb: 0.5
                                    }}
                                  >
                                    <Box 
                                      sx={{ 
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        height: '100%',
                                        width: `${parseFloat(score) * 100}%`,
                                        bgcolor: parseFloat(score) > 0.6 ? 'success.main' : 
                                                parseFloat(score) > 0.4 ? 'warning.main' : 'error.main',
                                        borderRadius: 5
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'right' }}>
                                    {(parseFloat(score) * 100).toFixed(0)}%
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Valuation Tab */}
              {tabValue === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Valuation Metrics</Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Price Multiples</Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>P/E Ratio</TableCell>
                                  <TableCell align="right">{fundamentalData.valuation.peRatio}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Forward P/E</TableCell>
                                  <TableCell align="right">{fundamentalData.valuation.forwardPe}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>PEG Ratio</TableCell>
                                  <TableCell align="right">{fundamentalData.valuation.pegRatio}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Price/Sales</TableCell>
                                  <TableCell align="right">{fundamentalData.valuation.priceToSales}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Price/Book</TableCell>
                                  <TableCell align="right">{fundamentalData.valuation.priceToBook}</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Enterprise Valuation</Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>Enterprise Value</TableCell>
                                  <TableCell align="right">{fundamentalData.valuation.enterpriseValue}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>EV/Revenue</TableCell>
                                  <TableCell align="right">{fundamentalData.valuation.evToRevenue}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>EV/EBITDA</TableCell>
                                  <TableCell align="right">{fundamentalData.valuation.evToEbitda}</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Analyst Ratings</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">Consensus</Typography>
                              <Chip 
                                label={fundamentalData.ratings.consensus} 
                                color={fundamentalData.ratings.consensus === 'Buy' ? 'success' : 
                                       fundamentalData.ratings.consensus === 'Sell' ? 'error' : 'warning'}
                                size="small"
                              />
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">Target Price</Typography>
                              <Typography variant="h6">
                                ${fundamentalData.ratings.targetPrice.average} 
                                <Typography component="span" variant="caption" color="text.secondary">
                                  (Current: ${fundamentalData.ratings.targetPrice.current})
                                </Typography>
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Rating Distribution</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" sx={{ width: 40 }}>Buy</Typography>
                                <Box sx={{ flexGrow: 1, mx: 1 }}>
                                  <Box 
                                    sx={{ 
                                      bgcolor: 'success.main', 
                                      height: 8, 
                                      width: `${(fundamentalData.ratings.distribution.buy / (fundamentalData.ratings.distribution.buy + fundamentalData.ratings.distribution.hold + fundamentalData.ratings.distribution.sell)) * 100}%`, 
                                      borderRadius: 1 
                                    }} 
                                  />
                                </Box>
                                <Typography variant="body2">{fundamentalData.ratings.distribution.buy}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2" sx={{ width: 40 }}>Hold</Typography>
                                <Box sx={{ flexGrow: 1, mx: 1 }}>
                                  <Box 
                                    sx={{ 
                                      bgcolor: 'warning.main', 
                                      height: 8, 
                                      width: `${(fundamentalData.ratings.distribution.hold / (fundamentalData.ratings.distribution.buy + fundamentalData.ratings.distribution.hold + fundamentalData.ratings.distribution.sell)) * 100}%`, 
                                      borderRadius: 1 
                                    }} 
                                  />
                                </Box>
                                <Typography variant="body2">{fundamentalData.ratings.distribution.hold}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ width: 40 }}>Sell</Typography>
                                <Box sx={{ flexGrow: 1, mx: 1 }}>
                                  <Box 
                                    sx={{ 
                                      bgcolor: 'error.main', 
                                      height: 8, 
                                      width: `${(fundamentalData.ratings.distribution.sell / (fundamentalData.ratings.distribution.buy + fundamentalData.ratings.distribution.hold + fundamentalData.ratings.distribution.sell)) * 100}%`, 
                                      borderRadius: 1 
                                    }} 
                                  />
                                </Box>
                                <Typography variant="body2">{fundamentalData.ratings.distribution.sell}</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Price Targets</Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="error.main">${fundamentalData.ratings.targetPrice.low}</Typography>
                                <Typography variant="body2" color="text.secondary">Current: ${fundamentalData.ratings.targetPrice.current}</Typography>
                                <Typography variant="body2" color="success.main">${fundamentalData.ratings.targetPrice.high}</Typography>
                              </Box>
                              <Box sx={{ position: 'relative', height: 8, bgcolor: 'background.paper', my: 1, borderRadius: 1 }}>
                                <Box 
                                  sx={{ 
                                    position: 'absolute',
                                    left: `${((parseFloat(fundamentalData.ratings.targetPrice.low) - parseFloat(fundamentalData.ratings.targetPrice.low)) / (parseFloat(fundamentalData.ratings.targetPrice.high) - parseFloat(fundamentalData.ratings.targetPrice.low))) * 100}%`,
                                    right: `${100 - ((parseFloat(fundamentalData.ratings.targetPrice.high) - parseFloat(fundamentalData.ratings.targetPrice.low)) / (parseFloat(fundamentalData.ratings.targetPrice.high) - parseFloat(fundamentalData.ratings.targetPrice.low))) * 100}%`,
                                    top: 0,
                                    height: '100%',
                                    bgcolor: 'primary.main',
                                    borderRadius: 1
                                  }}
                                />
                                <Box 
                                  sx={{ 
                                    position: 'absolute',
                                    left: `${((parseFloat(fundamentalData.ratings.targetPrice.current) - parseFloat(fundamentalData.ratings.targetPrice.low)) / (parseFloat(fundamentalData.ratings.targetPrice.high) - parseFloat(fundamentalData.ratings.targetPrice.low))) * 100}%`,
                                    top: -4,
                                    width: 3,
                                    height: 16,
                                    bgcolor: 'text.secondary',
                                    transform: 'translateX(-50%)'
                                  }}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {/* Peer Comparison Tab */}
              {tabValue === 3 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Peer Comparison</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Company</TableCell>
                          <TableCell align="right">P/E Ratio</TableCell>
                          <TableCell align="right">Market Cap</TableCell>
                          <TableCell align="right">Change (%)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Current stock */}
                        <TableRow sx={{ bgcolor: 'action.selected' }}>
                          <TableCell>
                            <Typography fontWeight="bold">{fundamentalData.symbol}</Typography>
                            <Typography variant="caption" color="text.secondary">{fundamentalData.name}</Typography>
                          </TableCell>
                          <TableCell align="right">{fundamentalData.valuation.peRatio}</TableCell>
                          <TableCell align="right">{fundamentalData.ratings.targetPrice.average * 5}B</TableCell>
                          <TableCell align="right">
                            <Typography 
                              color={parseFloat(fundamentalData.ratings.targetPrice.average) > parseFloat(fundamentalData.ratings.targetPrice.current) ? 'success.main' : 'error.main'}
                            >
                              {((parseFloat(fundamentalData.ratings.targetPrice.average) / parseFloat(fundamentalData.ratings.targetPrice.current) - 1) * 100).toFixed(2)}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                        
                        {/* Peer companies */}
                        {fundamentalData.peerComparison.map((peer) => (
                          <TableRow key={peer.symbol} hover>
                            <TableCell>
                              <Typography>{peer.symbol}</Typography>
                              <Typography variant="caption" color="text.secondary">{peer.name}</Typography>
                            </TableCell>
                            <TableCell align="right">{peer.peRatio}</TableCell>
                            <TableCell align="right">{peer.marketCap}</TableCell>
                            <TableCell align="right">
                              <Typography color={parseFloat(peer.priceChange) > 0 ? 'success.main' : 'error.main'}>
                                {peer.priceChange}%
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Enter a Symbol to Begin
          </Typography>
          <Typography variant="body1">
            Enter a stock symbol to see fundamental analysis data.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default FundamentalAnalysis;
